import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export interface User {
  id: string;
  email: string;
  name: string;
  workspaceId: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrl + '/v1';
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      this.currentUserSubject.next(JSON.parse(user));
    }
  }

  register(data: RegisterData): Observable<{ success: boolean; data: { user: User; tokens: AuthTokens } }> {
    return this.http.post<{ success: boolean; data: { user: User; tokens: AuthTokens } }>(
      `${this.API_URL}/auth/register`,
      data
    ).pipe(
      tap(response => {
        if (response.success) {
          this.setAuthData(response.data.user, response.data.tokens);
        }
      })
    );
  }

  login(data: LoginData): Observable<{ success: boolean; data: { user: User; tokens: AuthTokens } }> {
    return this.http.post<{ success: boolean; data: { user: User; tokens: AuthTokens } }>(
      `${this.API_URL}/auth/login`,
      data
    ).pipe(
      tap(response => {
        if (response.success) {
          this.setAuthData(response.data.user, response.data.tokens);
        }
      })
    );
  }

  logout(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    this.currentUserSubject.next(null);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    return !!this.getCurrentUser();
  }

  getAuthToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private setAuthData(user: User, tokens: AuthTokens): void {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }
} 